import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Chip,
  Box,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import { format } from "date-fns";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import TaskCalendar from "../task-calendar";
import TaskHistoryExport from "./export-options";
import SelectedTaskDetails from "./selected-task-details";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import Loading from "../../../common/loader/loading";

const TaskHistory = () => {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]); // Tasks after filtering and sorting
  const [loading, setLoading] = useState(true);
  const [openCalendar, setOpenCalendar] = useState(true); // Toggle calendar
  const [selectedTask, setSelectedTask] = useState(null); // State for selected task

  // Sorting and filtering states
  const [sortAnchorEl, setSortAnchorEl] = useState(null); // Anchor for the Sort menu
  const [filterAnchorEl, setFilterAnchorEl] = useState(null); // Anchor for the Filter menu
  const [sortField, setSortField] = useState(""); // Current field to black by
  const [sortDirection, setSortDirection] = useState("asc"); // Current black direction
  const [filters, setFilters] = useState({
    taskName: "",
    status: "",
    updated: "",
  });
  // calendar
  const [currentDate, setCurrentDate] = useState(new Date()); // Current month and year
  const [selectedDates, setSelectedDates] = useState([]); // Selected dates

  // Handle month navigation
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Handle date selection
  const handleSelectedDatesUpdate = (dates) => {
    setSelectedDates(dates);

    if (dates.length === 0) {
      setFilteredTasks(tasks); // Show all tasks if no date range is selected
      return;
    }

    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[dates.length - 1]);

    const filtered = tasks.filter((task) => {
      const taskDate = new Date(task.updated_at); // Ensure consistent date parsing
      return taskDate >= startDate && taskDate <= endDate;
    });

    setFilteredTasks(filtered);
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: {
          "x-user-role": 2 || 3 || 4, // Custom header
        },
      });
      setTasks(response.data);
      setFilteredTasks(response.data); // Initialize filteredTasks
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  // Handle sorting
  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortDirection(direction);

    const sortedTasks = [...filteredTasks].sort((a, b) => {
      if (field === "updated_at") {
        return direction === "asc" ? new Date(a[field]) - new Date(b[field]) : new Date(b[field]) - new Date(a[field]);
      }
      if (a[field] < b[field]) return direction === "asc" ? -1 : 1;
      if (a[field] > b[field]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredTasks(sortedTasks);
    setSortAnchorEl(null); // Close menu
  };

  // Handle filtering
  const handleFilter = () => {
    const filtered = tasks.filter((task) => {
      const matchesTaskName = filters.taskName
        ? task.taskName.toLowerCase().includes(filters.taskName.toLowerCase())
        : true;
      const matchesStatus = filters.status ? task.status.toLowerCase().includes(filters.status.toLowerCase()) : true;
      const matchesUpdated = filters.updated
        ? format(new Date(task.updated_at), "dd MMM yyyy").includes(filters.updated)
        : true;
      return matchesTaskName && matchesStatus && matchesUpdated;
    });
    setFilteredTasks(filtered);
    setFilterAnchorEl(null); // Close the filter menu
  };

  const clearFilters = () => {
    setFilters({
      taskName: "",
      status: "",
      updated: "",
    });
    setFilteredTasks(tasks); // Reset to full list
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Grid container className='task-container'>
      {/* Update Grid items for better tablet responsiveness */}
      <Grid
        item
        xs={12}
        md={8}
        lg={8}
        className='task-list'
        sx={{
          width: "100%",
          overflowX: { xs: "auto", sm: "hidden" },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            ml: { xs: 1, sm: 2 },
            mr: { xs: 1, sm: 1 },
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            <Grid container>
              <Grid item>
                <KeyboardBackspaceIcon />
              </Grid>
              <Grid item> Back to home </Grid>
            </Grid>
          </Typography>
          <Box mt={3} display='flex' justifyContent='space-between' mb={2}>
            <Box>
              <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }} align='left' mt={1}>
                {"Tasks > Tasks History"}
              </Typography>
            </Box>
            {/* Update buttons container for tablet */}
            <Box
              display='flex'
              gap={2}
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
              }}
            >
              <Button
                fullWidth={false}
                sx={{
                  minWidth: { xs: "100%", sm: "auto" },
                }}
                variant='outlined'
                color='primary'
                onClick={(e) => setSortAnchorEl(e.currentTarget)}
              >
                <SwapVertIcon />
                Sort
              </Button>
              <Button variant='outlined' color='primary' onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
                <FilterAltOutlinedIcon />
                Filter
              </Button>
              <Button
                variant='outlined'
                color='primary'
                onClick={() => {
                  setOpenCalendar(true);
                  setSelectedTask(null);
                }}
              >
                <CalendarMonthOutlinedIcon />
                Calendar
              </Button>
            </Box>
          </Box>

          {/* Sort Menu */}
          <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={() => setSortAnchorEl(null)}>
            <MenuItem onClick={() => handleSort("taskName")}>Task Name</MenuItem>
            <MenuItem onClick={() => handleSort("status")}>Status</MenuItem>
            <MenuItem onClick={() => handleSort("updated_at")}>Updated</MenuItem>
          </Menu>

          {/* Filter Menu */}
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={() => setFilterAnchorEl(null)}
            PaperProps={{
              sx: {
                width: { xs: "90vw", sm: "500px", md: "500px" },
                maxHeight: "80vh",
              },
            }}
          >
            <Box p={2} width={500}>
              <Typography variant='subtitle1' mb={1}>
                Filter by
              </Typography>
              <Grid container spacing={2}>
                {/* Task Name Dropdown */}
                <Grid item xs={6}>
                  <Autocomplete
                    id='taskName'
                    options={[...new Set(tasks.map((item) => item.taskName))]} // Get unique task names from tasks
                    value={filters.taskName}
                    onChange={(event, newValue) => setFilters({ ...filters, taskName: newValue || "" })}
                    renderInput={(params) => (
                      <>
                        <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                          Task Name
                        </Typography>
                        <TextField {...params} placeholder='Select Task Name' />
                      </>
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                {/* Status Dropdown */}
                <Grid item xs={6}>
                  <Autocomplete
                    id='status'
                    options={[...new Set(tasks.map((item) => item.status))]} // Get unique statuses from tasks
                    value={filters.status}
                    onChange={(event, newValue) => setFilters({ ...filters, status: newValue || "" })}
                    renderInput={(params) => (
                      <>
                        <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                          Status
                        </Typography>
                        <TextField {...params} placeholder='Select Status' />
                      </>
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </Grid>

                {/* Updated Dropdown */}
                <Grid item xs={6}>
                  <Autocomplete
                    id='updated'
                    options={[...new Set(tasks.map((item) => format(new Date(item.updated_at), "dd MMM yyyy")))]} // Get unique updated dates
                    value={filters.updated}
                    onChange={(event, newValue) => setFilters({ ...filters, updated: newValue || "" })}
                    renderInput={(params) => (
                      <>
                        <Typography sx={{ fontSize: "14px", color: "black" }} align='left'>
                          Updated
                        </Typography>
                        <TextField {...params} placeholder='Select Updated Date' />
                      </>
                    )}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                </Grid>
              </Grid>

              <Grid item xs={12} mt={2}>
                <Grid container spacing={1} className='submit-button-container'>
                  <Grid item>
                    <Button variant='outlined' color='primary' onClick={clearFilters} sx={{ textTransform: "none" }}>
                      Clear
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant='contained' color='primary' onClick={handleFilter} sx={{ textTransform: "none" }}>
                      Apply
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Menu>
          {/* Update table container for tablet */}
          <Box
            className='scrollable-task-list'
            sx={{
              overflowX: "auto",
              "& .MuiTable-root": {
                minWidth: { xs: 650, sm: "100%" },
              },
              "& .MuiTableCell-root": {
                px: { xs: 1, sm: 2 },
                py: { xs: 1, sm: 1.5 },
              },
            }}
          >
            <Table>
              {/* Table Head */}
              <TableHead>
                <TableRow>
                  <TableCell>Task Name</TableCell>
                  <TableCell>Work Shift</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>

              {/* Table Body */}
              <TableBody>
                {filteredTasks.map((row) => (
                  <TableRow
                    key={row.id}
                    onClick={() => {
                      setSelectedTask(row);
                      setOpenCalendar(false);
                    }} // Update selectedTask on row click
                    sx={{
                      cursor: "pointer",
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    {/* Task Name */}
                    <TableCell>
                      <Typography sx={{ fontSize: "13px", color: "black" }}>{row.taskName}</Typography>
                      <Typography sx={{ fontSize: "12px" }}>{row.description}</Typography>
                    </TableCell>

                    {/* Work Shift */}
                    <TableCell>
                      <Chip
                        label={row.shiftType}
                        size='small'
                        variant='outlined'
                        sx={{ marginRight: 1, backgroundColor: "#ECECED", border: "none" }}
                      />
                    </TableCell>

                    {/* Task Status */}
                    <TableCell>
                      <Chip
                        sx={{
                          fontSize: "12px",
                          borderRadius: 5,
                          backgroundColor:
                            row.status === "completed"
                              ? "#EAFFF2"
                              : row.status === "incompleted"
                              ? "#FFDDCC"
                              : "#F2F6FF",
                          color: row.status === "completed" ? "green" : row.status === "incompleted" ? "red" : "blue",
                        }}
                        label={row.status}
                        size='small'
                      />
                    </TableCell>

                    {/* Updated */}
                    <TableCell>
                      <Box display='flex' alignItems='center'>
                        <Typography variant='body2'>{format(new Date(row.updated_at), "dd MMM yyyy")}</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Grid>
      {/* Update right sidebar for tablet */}
      <Grid
        item
        xs={12}
        md={4}
        lg={4}
        sx={{
          borderLeft: { xs: "none", md: "1px solid" },
          borderTop: { xs: "1px solid", md: "none" },
          borderColor: "divider",
        }}
      >
        {openCalendar && (
          <>
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                mr: { xs: 1, sm: 2 },
                ml: { xs: 1, sm: 1 },
              }}
            >
              <TaskCalendar
                currentDate={currentDate}
                selectedDates={selectedDates}
                handlePrevMonth={handlePrevMonth}
                handleNextMonth={handleNextMonth}
                handleSelectedDatesUpdate={handleSelectedDatesUpdate}
              />
            </Box>
            <Box
              sx={{
                p: 2,
                mx: { xs: 1, sm: 2 },
                mt: { xs: 2, sm: 3 },
              }}
            >
              <TaskHistoryExport filteredTasks={filteredTasks} />
            </Box>
          </>
        )}
        {selectedTask && (
          <>
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                mx: { xs: 1, sm: 2 },
              }}
            >
              <SelectedTaskDetails selectedTask={selectedTask} />
            </Box>
            <Box
              sx={{
                p: 2,
                mx: { xs: 1, sm: 2 },
                mt: { xs: 2, sm: 3 },
              }}
            >
              <TaskHistoryExport filteredTasks={filteredTasks} />
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default TaskHistory;
